import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HelpCircle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CardActions } from "@/components/CardActions";

export function Pourquoi() {
  const { t, i18n } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/why", { params: { refresh, lang: i18n.language } });
      setItem({ question: data.question, answer: data.answer });
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.whyError"));
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [i18n.language]); // eslint-disable-line

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      data-testid="pourquoi"
      className="max-w-3xl mx-auto glass stats-wow rounded-2xl px-7 py-9 text-center"
    >
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <HelpCircle className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-5">{t("atelier.whyTitle")}</p>
        {loading && !item ? (
          <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t("atelier.whyLoading")}
          </p>
        ) : item ? (
          <>
            <p className="font-display text-xl sm:text-2xl text-gradient-orange leading-snug max-w-2xl mx-auto mb-4" data-testid="why-question">
              {item.question}
            </p>
            <p className="font-display italic text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto" data-testid="why-answer">
              {item.answer}
            </p>
          </>
        ) : (
          <p className="font-display italic text-foreground/40">{t("atelier.whyError")}</p>
        )}
        <button
          onClick={() => load(true)} disabled={loading}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
          data-testid="why-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {t("atelier.whyNew")}
        </button>
        {item && <CardActions cardId="why" heading={item.question} testId="why" />}
      </div>
    </motion.div>
  );
}
